import React, { useLayoutEffect, useState } from 'react';
import styled from '@emotion/styled';
import { BsPlusSquare } from '@react-icons/all-files/bs/BsPlusSquare';
import { compareAsc } from 'date-fns';
import { BucketLogData } from '@bucketplace/bucket-log';

import { Duration, EditorPlaceholder } from '@/components';
import { useEditorNode, useImpressionLog } from '@/hooks';
import { DocumentPath } from '@/types';

import { BoxModelValue } from './BlockInspector/BoxModelForm/types';

export interface BlockProps extends BoxModelValue {
  children?: React.ReactNode;
  background?: string;
  duration?: Duration;
  path?: DocumentPath;
  logData?: BucketLogData;
}

export function Block({ children, duration, path, logData, ...props }: BlockProps) {
  const [onDuration, setOnDuration] = useState<boolean>(false);
  const { displayMode, displayDate } = useEditorNode(path);

  const { ref } = useImpressionLog({
    logData,
    disable: displayMode === 'edit',
  });

  useLayoutEffect(() => {
    if (!duration) { return setOnDuration(true); }
    let interval: number;

    const compareDisplayDateToDuration = () => {
      const currentDate = displayDate ?? new Date();

      if (duration.startAt) {
        const beforeStart = compareAsc(currentDate, new Date(duration.startAt)) < 0;
        if (beforeStart) {
          return setOnDuration(false);
        }
      }
      if (duration.endAt) {
        const beforeEnd = compareAsc(currentDate, new Date(duration.endAt)) < 0;
        if (beforeEnd) {
          return setOnDuration(true);
        }
        setOnDuration(false);
        clearInterval(interval);
        return;
      }

      setOnDuration(true);
      clearInterval(interval);
    };

    compareDisplayDateToDuration();
    interval = window.setInterval(compareDisplayDateToDuration, 1000);
    return () => clearInterval(interval);
  }, [duration?.startAt, duration?.endAt, displayDate]);

  if (!onDuration && displayMode !== 'edit') { return null; }

  return (
    <Box ref={ref} {...props}>
      { children || (
        <EditorPlaceholder
          icon={<BsPlusSquare />}
          message="아이템을 추가해주세요."
        />
      ) }
    </Box>
  );
}

const Box = styled.div<Omit<BlockProps, 'children'>>`
  margin: ${({ margin }) => (margin
    ? `${margin.top}px ${margin.right}px ${margin.bottom}px ${margin.left}px`
    : 0)};
  padding: ${({ padding }) => (padding
    ? `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`
    : 0)};
  border: ${({ border }) => (border ? `${border.width}px solid ${border.color}` : 'none')};
  border-radius: ${({ border }) => (border ? `${border.radius}px` : 0)};
  background: ${({ background }) => background || 'none'};
`;
